import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { BotanicalCorner, GoldLineDivider } from '@/components/feature/DrawingLines';

const REGIONS = ['서울', '경기/인천', '부산/경남', '대구/경북', '대전/충청', '광주/전라'];

const regionStores: Record<string, { name: string; district: string; area: string; artists: number }[]> = {
  '서울': [
    { name: '선율 청담 플래그십', district: '강남구 청담', area: '15평', artists: 4 },
    { name: '선율 홍대점', district: '마포구 홍대', area: '8평', artists: 2 },
    { name: '선율 강남역점', district: '서초구 강남역', area: '11평', artists: 3 },
    { name: '선율 이태원점', district: '용산구 이태원', area: '13평', artists: 3 },
    { name: '선율 성수점', district: '성동구 성수', area: '10평', artists: 2 },
  ],
  '경기/인천': [
    { name: '선율 분당점', district: '성남시 분당', area: '12평', artists: 3 },
    { name: '선율 인천 송도점', district: '인천 연수구 송도', area: '9평', artists: 2 },
    { name: '선율 수원점', district: '수원시 영통', area: '8평', artists: 2 },
  ],
  '부산/경남': [
    { name: '선율 부산 해운대점', district: '해운대구', area: '12평', artists: 3 },
    { name: '선율 부산 서면점', district: '부산진구 서면', area: '9평', artists: 2 },
  ],
  '대구/경북': [
    { name: '선율 대구 동성로점', district: '중구 동성로', area: '10평', artists: 2 },
  ],
  '대전/충청': [
    { name: '선율 대전 둔산점', district: '서구 둔산', area: '11평', artists: 3 },
  ],
  '광주/전라': [
    { name: '선율 광주 충장로점', district: '동구 충장로', area: '9평', artists: 2 },
  ],
};

const regionImages: Record<string, string> = {
  '서울': 'https://public.readdy.ai/ai/img_res/999684ddc223de22b05a3666623d7adf.jpg',
  '경기/인천': 'https://readdy.ai/api/search-image?query=Incheon%20Songdo%20international%20city%20Korea%20modern%20skyscrapers%20central%20park%20aerial%20view%20night%20lights%20urban%20landscape%20cinematic%20photography&width=900&height=600&seq=region_gyeonggi_v2&orientation=landscape',
  '부산/경남': 'https://readdy.ai/api/search-image?query=Busan%20Korea%20Haeundae%20beach%20coastline%20city%20skyline%20at%20sunset%20golden%20light%20ocean%20view%20cinematic%20wide%20angle%20photography%20premium%20atmosphere&width=900&height=600&seq=region_busan_v2&orientation=landscape',
  '대구/경북': 'https://readdy.ai/api/search-image?query=Daegu%20Korea%20downtown%20Dongseongno%20street%20night%20lights%20urban%20cityscape%20modern%20buildings%20cinematic%20photography%20moody%20atmosphere&width=900&height=600&seq=region_daegu_v2&orientation=landscape',
  '대전/충청': 'https://readdy.ai/api/search-image?query=Daejeon%20Korea%20Dunsan%20district%20modern%20city%20buildings%20night%20lights%20urban%20landscape%20cinematic%20photography%20premium%20atmosphere&width=900&height=600&seq=region_daejeon_v2&orientation=landscape',
  '광주/전라': 'https://readdy.ai/api/search-image?query=Gwangju%20Korea%20city%20center%20Chungjangno%20street%20night%20lights%20urban%20landscape%20modern%20buildings%20cinematic%20photography%20moody%20atmosphere&width=900&height=600&seq=region_gwangju_v2&orientation=landscape',
};

export default function StorefinderSection() {
  const { ref, visible } = useScrollAnimation(0.05);
  const [activeRegion, setActiveRegion] = useState('서울');
  const navigate = useNavigate();

  const currentStores = regionStores[activeRegion] ?? [];
  const currentImage = regionImages[activeRegion];

  return (
    <section className="py-24 md:py-36 bg-[#FAFAFA] overflow-x-hidden relative" ref={ref}>
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.08)" size={160} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.07)" size={140} />

      <div className="max-w-[1440px] mx-auto px-4 md:px-16 w-full">
        {/* 헤더 */}
        <div
          className="text-center mb-10 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <p
            className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5"
            style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
          >
            Find a Studio
          </p>
          <h2
            className="text-3xl md:text-4xl text-[#222222] mb-5"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
          >
            가까운 선율을 찾아보세요
          </h2>
          <p
            className="text-sm text-[#888] max-w-md mx-auto leading-relaxed"
            style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}
          >
            전국 47개 가맹점에서 동일한 프리미엄 경험을 만나보세요.
          </p>
        </div>

        <div
          className="mb-12 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transitionDelay: '100ms' }}
        >
          <GoldLineDivider delay={0.2} />
        </div>

        {/* 메인 콘텐츠 */}
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-0 transition-all duration-1000 ease-out w-full min-w-0"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)', transitionDelay: '200ms' }}
        >
          {/* 왼쪽: 지역 선택 + 지점 목록 */}
          <div className="bg-white border border-[#EBEBEB] flex flex-col min-w-0 overflow-hidden">
            {/* 지역 탭 */}
            <div className="border-b border-[#EBEBEB] overflow-x-auto scrollbar-none">
              <div className="flex w-max min-w-full">
                {REGIONS.map((region) => (
                  <button
                    key={region}
                    onClick={() => setActiveRegion(region)}
                    className={`flex-1 px-3 md:px-5 py-4 text-[10px] md:text-[11px] tracking-[0.05em] md:tracking-[0.1em] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border-b-2 ${
                      activeRegion === region
                        ? 'border-[#C9A96E] text-[#1A1A1A] bg-[#FAFAFA]'
                        : 'border-transparent text-[#AAAAAA] hover:text-[#555] hover:bg-[#FAFAFA]'
                    }`}
                    style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}
                  >
                    {region}
                    <span
                      className={`ml-1 text-[9px] px-1 py-0.5 rounded-full ${
                        activeRegion === region ? 'bg-[#C9A96E] text-white' : 'bg-[#F0F0F0] text-[#AAAAAA]'
                      }`}
                    >
                      {regionStores[region]?.length ?? 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* 지점 목록 */}
            <div className="flex-1 divide-y divide-[#F5F5F5]">
              {currentStores.map((store, i) => (
                <div
                  key={store.name}
                  className="flex items-center justify-between px-6 py-4 hover:bg-[#FAFAFA] transition-colors duration-200 group cursor-pointer"
                  style={{
                    opacity: visible ? 1 : 0,
                    transition: 'opacity 0.5s ease-out, background-color 0.2s',
                    transitionDelay: `${i * 60 + 300}ms`,
                  }}
                  onClick={() => navigate('/franchise/stores')}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 flex items-center justify-center border border-[#EBEBEB] group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E] transition-all duration-200 flex-shrink-0">
                      <i className="ri-map-pin-line text-[#AAAAAA] group-hover:text-white text-xs transition-colors duration-200" />
                    </div>
                    <div className="min-w-0">
                      <p
                        className="text-sm text-[#1A1A1A] font-medium leading-tight group-hover:text-[#C9A96E] transition-colors duration-200 truncate"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                      >
                        {store.name}
                      </p>
                      <p className="text-[11px] text-[#AAAAAA] mt-0.5 truncate" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                        {store.district}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                    <div className="text-right hidden sm:block">
                      <p className="text-[10px] text-[#888]">{store.area}</p>
                      <p className="text-[10px] text-[#AAAAAA]">아티스트 {store.artists}명</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-[#DDDDDD] group-hover:text-[#C9A96E] transition-colors duration-200" />
                  </div>
                </div>
              ))}
            </div>

            {/* 전체 보기 버튼 */}
            <div className="p-5 border-t border-[#EBEBEB]">
              <Link
                to="/franchise/stores"
                className="flex items-center justify-center gap-2 w-full py-3 border border-[#EBEBEB] text-[10px] tracking-[0.2em] uppercase text-[#888] hover:border-[#C9A96E] hover:text-[#C9A96E] transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                전국 가맹점 전체 보기
                <i className="ri-arrow-right-line text-xs" />
              </Link>
            </div>
          </div>

          {/* 오른쪽: 지역 이미지 */}
          <div className="relative overflow-hidden" style={{ minHeight: '420px' }}>
            <img
              key={activeRegion}
              src={currentImage}
              alt={`${activeRegion} 지역`}
              className="absolute inset-0 w-full h-full object-cover object-center"
              style={{
                animation: 'fadeInScale 0.6s ease-out forwards',
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/20 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/30 to-transparent" />

            {/* 지역 정보 오버레이 */}
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <p
                className="text-[10px] tracking-[0.35em] uppercase text-[#C9A96E] mb-2"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {activeRegion}
              </p>
              <p
                className="text-2xl md:text-3xl text-white mb-3"
                style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300 }}
              >
                {currentStores.length}개 지점 운영 중
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <i className="ri-user-line text-[#C9A96E] text-xs" />
                  <span className="text-xs text-white/60" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    아티스트 {currentStores.reduce((acc, s) => acc + s.artists, 0)}명
                  </span>
                </div>
                <div className="w-px h-3 bg-white/20" />
                <Link
                  to="/franchise/stores"
                  className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors duration-200 cursor-pointer"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  상세 보기
                  <i className="ri-arrow-right-line text-xs" />
                </Link>
              </div>
            </div>

            {/* 지역 인디케이터 */}
            <div className="absolute top-6 right-6 flex flex-col gap-1.5">
              {REGIONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setActiveRegion(r)}
                  className="cursor-pointer transition-all duration-200"
                  title={r}
                >
                  <span
                    className="block rounded-full transition-all duration-300"
                    style={{
                      width: r === activeRegion ? '20px' : '6px',
                      height: '6px',
                      background: r === activeRegion ? '#C9A96E' : 'rgba(255,255,255,0.3)',
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 하단 CTA 배너 */}
        <div
          className="mt-8 p-5 md:p-8 bg-[#1A1A1A] flex flex-col md:flex-row items-center justify-between gap-5 transition-all duration-1000 ease-out w-full"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '500ms' }}
        >
          <div className="flex items-center gap-5">
            <div className="w-10 h-10 flex items-center justify-center border border-[#C9A96E]/40 flex-shrink-0">
              <i className="ri-store-2-line text-[#C9A96E] text-lg" />
            </div>
            <div>
              <p
                className="text-sm text-white font-medium mb-0.5"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
              >
                내 지역에 선율이 없다면?
              </p>
              <p className="text-xs text-white/40" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                가맹 문의를 통해 새로운 지점을 오픈하세요.
              </p>
            </div>
          </div>
          <Link
            to="/community/inquiry"
            className="flex-shrink-0 px-8 py-3 bg-[#C9A96E] text-white text-[10px] tracking-[0.2em] uppercase font-medium hover:bg-[#b8955a] transition-colors duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            가맹 문의하기
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(1.04); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </section>
  );
}
